import type { HIInvoiceBillingItem, HIInvoiceEditRequest } from '@/types/homeInspection.types';

export type HiInvoiceBillingRow = {
  id: string;
  billingItemId: string | null;
  itemId: string;
  description: string;
  quantity: number;
  rate: number;
  lineTotal: number;
};

export const mapInvoiceItemsToRows = (items: HIInvoiceBillingItem[]): HiInvoiceBillingRow[] =>
  items.map((item) => ({
    id: item.id,
    billingItemId: item.id,
    itemId: item.itemId,
    description: item.description,
    quantity: item.quantity,
    rate: item.rate,
    lineTotal: item.lineTotal,
  }));

export const buildHiInvoiceBillingPatch = (
  rows: HiInvoiceBillingRow[],
  originalItems: HIInvoiceBillingItem[]
): NonNullable<HIInvoiceEditRequest['billingItems']> => {
  const originalIds = originalItems.map((item) => item.id);
  const currentIds = rows
    .map((row) => row.billingItemId)
    .filter((id): id is string => Boolean(id));

  const create = rows
    .filter((row) => !row.billingItemId)
    .map((row) => ({
      itemId: row.itemId,
      quantity: row.quantity,
      rate: row.rate,
    }));

  const update: NonNullable<NonNullable<HIInvoiceEditRequest['billingItems']>['update']> = [];
  rows.forEach((row) => {
    if (!row.billingItemId) return;
    const original = originalItems.find((item) => item.id === row.billingItemId);
    if (!original) return;
    if (original.quantity !== row.quantity || Math.abs(original.rate - row.rate) > 0) {
      update.push({
        id: row.billingItemId,
        quantity: row.quantity,
        rate: row.rate,
      });
    }
  });

  const remove = originalIds.filter((id) => !currentIds.includes(id));

  return {
    ...(create.length > 0 ? { create } : {}),
    ...(update.length > 0 ? { update } : {}),
    ...(remove.length > 0 ? { delete: remove } : {}),
  };
};

export const hasHiInvoiceBillingChanges = (
  rows: HiInvoiceBillingRow[],
  originalItems: HIInvoiceBillingItem[]
): boolean => {
  const patch = buildHiInvoiceBillingPatch(rows, originalItems);
  return (
    (patch.create?.length ?? 0) > 0 ||
    (patch.update?.length ?? 0) > 0 ||
    (patch.delete?.length ?? 0) > 0
  );
};
